/* eslint-disable @typescript-eslint/no-explicit-any */
import useRequest from "@/lib/request/useRequest";
import { Plot } from "@/types/plot";
import { AsAppliedPlot } from "@/types/asAppliedPlot";
import {
    Button,
    Checkbox,
    getKeyValue,
    Slider,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Input,
} from "@heroui/react";
import { useParams } from "@tanstack/react-router";
import dayjs, { Dayjs } from "dayjs";
import { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { PlotType } from "@/types/enums";
import useMap from "@/lib/map/useMap";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { ImageSource, RasterLayerSpecification } from "mapbox-gl";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { APPLICATION_OPERATION_IMAGE_LAYER } from "@/lib/map/layers";
import {
    removeLayersIfExists,
    removeSourcesIfExists,
    updateLayerOpacity,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
    polygonShortSide,
    polygonDistance,
    polygonsOverlap,
} from "@/lib/utils/map";
import { faSave, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import useAuth from "@/lib/auth/useAuth";
import { useI18n } from "@/lib/i18n/useI18n";
import useFeatureFlags from "@/lib/featureFlags/useFeatureFlags";
import * as turf from "@turf/turf";

type PlotTableRow = {
    pairId: number;
    referencePlot?: Feature<MultiPolygon, Plot>;
    canaryPlot?: Feature<MultiPolygon, Plot>;
};

type ValidationPlot = Plot & {
    is_validation: true;
    plot_id: number;
    source?: "override" | "auto";
};

type ValidationPlotPlacement = {
    pair_id: number;
    plot_type: PlotType;
    type: string;
    source: "override" | "auto";
    canary_plot_id: number | null;
};

const VALIDATION_FEATURE_PREFIX = "validation-";

function isValidationFeature(feature: Feature) {
    if (
        feature.properties?.is_validation === true ||
        feature.properties?.is_validation === "true"
    ) {
        return true;
    }
    return String(feature.id ?? "").startsWith(VALIDATION_FEATURE_PREFIX);
}

function getValidationCanaryPlotId(
    feature: Feature,
    analyticPlots: Feature[] = [],
): number | null {
    const fromProps = feature.properties?.plot_id;
    if (
        fromProps != null &&
        fromProps !== "" &&
        Number.isFinite(Number(fromProps))
    ) {
        return Number(fromProps);
    }

    const featureId = String(feature.id ?? "");
    if (featureId.startsWith(VALIDATION_FEATURE_PREFIX)) {
        const parsed = Number(
            featureId.slice(VALIDATION_FEATURE_PREFIX.length),
        );
        if (Number.isFinite(parsed)) return parsed;
    }

    const pairId = feature.properties?.pair_id;
    if (pairId == null) return null;
    const canary = analyticPlots.find(
        (plot) =>
            Number(plot.properties?.pair_id) === Number(pairId) &&
            Number(plot.properties?.plot_type) === PlotType.Canary,
    );
    if (canary?.id != null && Number.isFinite(Number(canary.id))) {
        return Number(canary.id);
    }
    return null;
}

function toMultiPolygon(
    geometry: MultiPolygon | Polygon | null | undefined,
): MultiPolygon | null {
    if (!geometry) return null;
    if (geometry.type === "MultiPolygon") return geometry;
    if (geometry.type === "Polygon") {
        return {
            type: "MultiPolygon",
            coordinates: [geometry.coordinates],
        };
    }
    return null;
}

type ValidationIssue = {
    pairId: number;
    featureId: string | number;
    message: string;
    reason: "size" | "distance" | "intersection";
};

type PlotSetRow = {
    prescriptionId?: number;
    createdDate?: Dayjs;
    latestAppliedDate?: Dayjs;
    latestApplicationId?: number;
    activePlots: number;
    totalPlots: number;
    isSlices?: boolean;
    isSelected?: boolean;
    prescriptionName?: string;
    plots: Feature<MultiPolygon, Plot>[];
};

function groupPlotsByPrescription(
    data?: FeatureCollection<MultiPolygon, Plot>,
) {
    if (!data) return [];
    return data?.features.reduce(
        (acc: PlotSetRow[], plot: Feature<MultiPolygon, Plot>) => {
            let existingRow = acc.find(
                (row) => row.prescriptionId === plot.properties.prescription,
            );
            if (existingRow) {
                existingRow = {
                    ...existingRow,
                    activePlots:
                        existingRow.activePlots +
                        (plot.properties.is_active ? 1 : 0),
                    totalPlots: existingRow.totalPlots + 1,
                    plots: [...existingRow.plots, plot],
                };
            } else {
                existingRow = {
                    prescriptionId: plot.properties.prescription,
                    latestAppliedDate: dayjs(
                        plot.properties.latest_applied_date,
                    ),
                    latestApplicationId: plot.properties
                        .latest_application_id as number,
                    activePlots: plot.properties.is_active ? 1 : 0,
                    totalPlots: 1,
                    isSlices: !!plot.properties.start_degree,
                    prescriptionName: plot.properties
                        .prescription_name as string,
                    plots: [plot],
                    createdDate: dayjs(plot.properties.created_date),
                };
            }
            const newList = acc.filter(
                (row) => row.prescriptionId !== existingRow?.prescriptionId,
            );
            newList.push(existingRow as PlotSetRow);
            return newList;
        },
        [],
    );
}

function groupPlotsByCreatedDate(data?: FeatureCollection<MultiPolygon, Plot>) {
    if (!data) return [];
    return data.features.reduce(
        (acc: PlotSetRow[], plot: Feature<MultiPolygon, Plot>) => {
            const createdDate = dayjs(plot.properties.created_date);

            let existingRow = acc.find((row) =>
                createdDate.isSame(row.createdDate, "minute"),
            );

            if (existingRow) {
                existingRow = {
                    ...existingRow,
                    activePlots:
                        existingRow.activePlots +
                        (plot.properties.is_active ? 1 : 0),
                    totalPlots: existingRow.totalPlots + 1,
                    plots: [...existingRow.plots, plot],
                };
            } else {
                existingRow = {
                    createdDate: createdDate,
                    activePlots: plot.properties.is_active ? 1 : 0,
                    totalPlots: 1,
                    isSlices: !!plot.properties.start_degree,
                    plots: [plot],
                    isSelected: false,
                };
            }

            const newList = acc.filter(
                (row) =>
                    !dayjs(row.createdDate).isSame(
                        dayjs(existingRow?.createdDate),
                        "minute",
                    ),
            );
            newList.push(existingRow as PlotSetRow);
            return newList;
        },
        [],
    );
}

const VALIDATION_PLOT_COLOR = "#1F77B4";
const VALIDATION_INVALID_COLOR = "#FFFF00";
const IS_VALIDATION = ["==", ["get", "user_is_validation"], true];
const IS_VALIDATION_INVALID = [
    "all",
    IS_VALIDATION,
    ["==", ["get", "user_validation_invalid"], true],
];
const VALIDATION_MIN_AREA_RATIO = 0.5;
const VALIDATION_MAX_AREA_RATIO = 1.5;
const VALIDATION_MAX_DISTANCE_PLOT_WIDTHS = 3;

const DRAW_STYLES = [
    ...MapboxDraw.lib.theme,
    {
        id: "plot-fill",
        type: "fill",
        filter: ["all", ["==", "meta", "feature"]],
        paint: {
            "fill-opacity": [
                "case",
                IS_VALIDATION,
                0.45,
                ["get", "user_is_active"],
                0.5,
                0.2,
            ],
            "fill-color": [
                "case",
                IS_VALIDATION_INVALID,
                VALIDATION_INVALID_COLOR,
                IS_VALIDATION,
                VALIDATION_PLOT_COLOR,
                ["==", ["get", "user_plot_type"], 1],
                "#FF0000",
                "#00FF00",
            ],
        },
    },
    {
        id: "plot-label",
        type: "symbol",
        filter: ["all", ["==", "meta", "feature"]],
        layout: {
            "text-field": [
                "concat",
                [
                    "case",
                    IS_VALIDATION,
                    "Validation",
                    ["==", ["get", "user_plot_type"], 1],
                    "Canary",
                    "Reference",
                ],
                "\n",
                " Pair Id: ",
                ["get", "user_pair_id"],
            ],
            "text-size": 10,
        },
    },
    {
        id: "plot-outline",
        type: "line",
        filter: ["all", ["==", "meta", "feature"]],
        paint: {
            "line-color": [
                "case",
                ["==", ["get", "user_validation_invalid"], true],
                "#FFFF00",
                ["==", ["get", "user_flagged"], true],
                "yellow",
                "white",
            ],
            "line-width": [
                "case",
                ["==", ["get", "user_validation_invalid"], true],
                3,
                ["==", ["get", "user_flagged"], true],
                2,
                0,
            ],
        },
    },
];

const AS_APPLIED_SOURCE = "insights-settings-as-applied-source";
const AS_APPLIED_LAYER: RasterLayerSpecification = {
    ...APPLICATION_OPERATION_IMAGE_LAYER,
    source: AS_APPLIED_SOURCE,
    id: "insights-settings-as-applied-layer",
    paint: {
        "raster-opacity": 0.8,
    },
    layout: {
        ...APPLICATION_OPERATION_IMAGE_LAYER.layout,
        visibility: "visible",
    },
};

function ActivePlots() {
    const { t } = useTranslation("insights");
    const draw = useRef<MapboxDraw | null>(null);
    const { map, mapLoaded } = useMap();
    const { fieldId } = useParams({
        from: "/dashboard/fields/$fieldId/insights/settings",
    });
    const { useGetRequest, useMutateRequest } = useRequest();
    const { fullPlotOffsetEnabled } = useFeatureFlags();
    const { fieldBbox } = useFieldDashboard() ?? {};
    const { userIsSentinel } = useAuth() ?? {};
    const { formatUnit, isImperial } = useI18n();
    const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>(
        [],
    );
    const hasValidationIntersection = useMemo(
        () => validationIssues.some((issue) => issue.reason === "intersection"),
        [validationIssues],
    );

    const COLUMNS = [
        { key: "type", label: t("tableColumns.type") },
        { key: "uploadType", label: t("tableColumns.creationType") },
        { key: "rxName", label: t("tableColumns.rxName") },
        { key: "date", label: t("tableColumns.dateLastApplied") },
        { key: "active", label: t("tableColumns.plotsActive") },
    ];

    const PLOT_COLUMNS = [
        { key: "pairId", label: t("tableColumns.pairId") },
        {
            key: "canaryRate",
            label: `${t("tableColumns.canary")} (${formatUnit("lb_ac", "kg_ha")})`,
        },
        {
            key: "referenceRate",
            label: `${t("tableColumns.reference")} (${formatUnit("lb_ac", "kg_ha")})`,
        },
        { key: "active", label: t("tableColumns.active") },
    ];

    useEffect(() => {
        if (map && mapLoaded && !draw.current) {
            draw.current = new MapboxDraw({
                displayControlsDefault: false,
                controls: {
                    trash: true,
                },
                defaultMode: "simple_select",
                userProperties: true,
                styles: DRAW_STYLES,
            });

            map.addControl(draw.current, "top-left");
        }
        return () => {
            if (map && draw.current) {
                if (map.hasControl(draw.current)) {
                    map.removeControl(draw.current);
                }
                draw.current = null;
            }
        };
    }, [map, mapLoaded]);

    const uploadedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/uploaded_plots/`,
        params: { boundary: "analytic" },
    });
    const appliedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/applied_plots/`,
        params: { boundary: "analytic" },
    });

    const [showValidationPlots, setShowValidationPlots] = useState(false);
    const [selectedPlots, setSelectedPlots] = useState<
        Feature<MultiPolygon, Plot>[]
    >([]);

    const selectedPlotIds = useMemo(
        () =>
            selectedPlots
                .map((plot) => Number(plot.id))
                .filter((id) => Number.isFinite(id)),
        [selectedPlots],
    );

    const validationPlotsQuery = useGetRequest<
        FeatureCollection<MultiPolygon, ValidationPlotPlacement>
    >({
        url: `fields/${fieldId}/validation_plots/`,
        params:
            selectedPlotIds.length > 0
                ? { plot_ids: selectedPlotIds.join(",") }
                : undefined,
        enabled: showValidationPlots && selectedPlotIds.length > 0,
    });

    const validationPlacementsByCanaryId = useMemo(() => {
        const placements = new globalThis.Map<
            number,
            Feature<MultiPolygon, ValidationPlotPlacement>
        >();
        validationPlotsQuery.data?.features?.forEach((feature) => {
            const canaryPlotId = feature.properties?.canary_plot_id;
            if (canaryPlotId != null && feature.geometry) {
                placements.set(Number(canaryPlotId), feature);
            }
        });
        return placements;
    }, [validationPlotsQuery.data]);

    const buildValidationFeatures = useCallback(
        (plots: Feature<MultiPolygon, Plot>[]) =>
            plots
                .filter((plot) => plot.properties.plot_type === PlotType.Canary)
                .flatMap<Feature<MultiPolygon, ValidationPlot>>((plot) => {
                    const canaryId = Number(plot.id);
                    const placement =
                        validationPlacementsByCanaryId.get(canaryId);
                    if (!placement || !Number.isFinite(canaryId)) return [];

                    const geometry =
                        toMultiPolygon(
                            placement.geometry as MultiPolygon | Polygon,
                        ) ?? (placement.geometry as MultiPolygon);

                    return [
                        {
                            ...plot,
                            id: `${VALIDATION_FEATURE_PREFIX}${canaryId}`,
                            geometry,
                            properties: {
                                ...plot.properties,
                                is_validation: true,
                                plot_id: canaryId,
                                source: placement.properties.source,
                            },
                        },
                    ];
                }),
        [validationPlacementsByCanaryId],
    );

    const checkValidationPlots = useCallback(() => {
        if (!draw.current) {
            setValidationIssues([]);
            return;
        }

        const features = draw.current.getAll().features;
        const analyticPlots = features.filter(
            (feature) => !isValidationFeature(feature),
        );
        const validationPlots = features.filter(isValidationFeature);
        const issues: ValidationIssue[] = [];

        validationPlots.forEach((validationFeature) => {
            const featureId = validationFeature.id as string | number;
            const pairId = validationFeature.properties?.pair_id as number;
            const canaryPlotId = getValidationCanaryPlotId(
                validationFeature,
                analyticPlots,
            );

            const canary = analyticPlots.find(
                (plot) => Number(plot.id) === Number(canaryPlotId),
            );
            const reference = analyticPlots.find(
                (plot) =>
                    plot.properties?.pair_id === pairId &&
                    plot.properties?.plot_type === PlotType.Reference,
            );
            const pairPlots = [canary, reference].filter(Boolean) as Feature<
                MultiPolygon | Polygon
            >[];

            if (!pairPlots.length || !validationFeature.geometry) {
                draw.current?.setFeatureProperty(
                    String(featureId),
                    "validation_invalid",
                    false,
                );
                return;
            }

            const validationGeom = validationFeature as Feature<
                MultiPolygon | Polygon
            >;
            const validationArea = turf.area(validationGeom);
            const meanPairArea =
                pairPlots.reduce((sum, plot) => sum + turf.area(plot), 0) /
                pairPlots.length;
            const areaRatio =
                meanPairArea > 0 ? validationArea / meanPairArea : 0;

            const meanPlotWidth =
                pairPlots.reduce(
                    (sum, plot) => sum + polygonShortSide(plot),
                    0,
                ) / pairPlots.length;
            const distanceToPair = Math.min(
                ...pairPlots.map((plot) =>
                    polygonDistance(validationGeom, plot),
                ),
            );
            const maxDistance =
                meanPlotWidth * VALIDATION_MAX_DISTANCE_PLOT_WIDTHS;
            const overlapsPair = pairPlots.some((plot) =>
                polygonsOverlap(validationGeom, plot),
            );

            let invalid = false;

            if (overlapsPair) {
                invalid = true;
                issues.push({
                    pairId,
                    featureId,
                    reason: "intersection",
                    message: t("messages.validationPlotIntersectsPair", {
                        pairId,
                    }),
                });
            }

            if (areaRatio < VALIDATION_MIN_AREA_RATIO) {
                invalid = true;
                issues.push({
                    pairId,
                    featureId,
                    reason: "size",
                    message: t("messages.validationPlotTooSmall", {
                        pairId,
                        percent: Math.round(areaRatio * 100),
                        minPercent: Math.round(VALIDATION_MIN_AREA_RATIO * 100),
                    }),
                });
            } else if (areaRatio > VALIDATION_MAX_AREA_RATIO) {
                invalid = true;
                issues.push({
                    pairId,
                    featureId,
                    reason: "size",
                    message: t("messages.validationPlotTooLarge", {
                        pairId,
                        percent: Math.round(areaRatio * 100),
                        maxPercent: Math.round(VALIDATION_MAX_AREA_RATIO * 100),
                    }),
                });
            }

            if (!overlapsPair && distanceToPair > maxDistance) {
                invalid = true;
                const distanceDisplay = isImperial
                    ? Math.round(distanceToPair * 3.28084)
                    : Math.round(distanceToPair);
                const unit = isImperial ? "ft" : "m";
                issues.push({
                    pairId,
                    featureId,
                    reason: "distance",
                    message: t("messages.validationPlotTooFar", {
                        pairId,
                        distance: distanceDisplay,
                        unit,
                        maxWidths: VALIDATION_MAX_DISTANCE_PLOT_WIDTHS,
                    }),
                });
            }

            draw.current?.setFeatureProperty(
                String(featureId),
                "validation_invalid",
                invalid,
            );
        });

        setValidationIssues(issues);
    }, [isImperial, t]);

    const drawPlots = useCallback(
        (plots: Feature<MultiPolygon, Plot>[]) => {
            if (!draw.current) return;
            draw.current.deleteAll();
            draw.current.add({
                type: "FeatureCollection",
                features: showValidationPlots
                    ? [...plots, ...buildValidationFeatures(plots)]
                    : plots,
            });
            checkValidationPlots();
        },
        [buildValidationFeatures, checkValidationPlots, showValidationPlots],
    );

    useEffect(() => {
        if (!draw.current) return;
        const features = draw.current.getAll().features;
        const plots = features.filter(
            (feature) => !isValidationFeature(feature),
        );
        const hasValidationFeatures = plots.length !== features.length;

        if (!plots.length) return;
        if (!showValidationPlots && !hasValidationFeatures) return;

        draw.current.deleteAll();
        draw.current.add({
            type: "FeatureCollection",
            features: showValidationPlots
                ? [
                    ...plots,
                    ...buildValidationFeatures(
                        plots as Feature<MultiPolygon, Plot>[],
                    ),
                ]
                : plots,
        });
        checkValidationPlots();
    }, [buildValidationFeatures, checkValidationPlots, showValidationPlots]);

    useEffect(() => {
        if (!map || !mapLoaded) return;

        map.on("draw.update", checkValidationPlots);
        map.on("draw.delete", checkValidationPlots);

        return () => {
            map.off("draw.update", checkValidationPlots);
            map.off("draw.delete", checkValidationPlots);
        };
    }, [checkValidationPlots, map, mapLoaded]);

    const plotSets = useMemo(() => {
        if (!uploadedPlots.isFetched || !appliedPlots.isFetched) return [];

        return [
            ...groupPlotsByCreatedDate(uploadedPlots.data),
            ...groupPlotsByPrescription(appliedPlots.data),
        ].map((plotset, index) => {
            return {
                key: index.toString(),
                type: plotset.isSlices
                    ? t("tableColumns.slices")
                    : t("tableColumns.blocks"),
                rxName: plotset.prescriptionName,
                date: plotset.latestAppliedDate?.isValid()
                    ? plotset.latestAppliedDate.format("ll")
                    : "--",
                uploadType: plotset.prescriptionId
                    ? t("tableColumns.rx")
                    : t("tableColumns.uploaded"),
                active: t("activeOfTotal", {
                    active: plotset.activePlots,
                    total: plotset.totalPlots,
                }),
                plots: plotset.plots,
            };
        });
    }, [
        appliedPlots.data,
        appliedPlots.isFetched,
        uploadedPlots.data,
        uploadedPlots.isFetched,
        t,
    ]);

    const validationFeatureCount = useMemo(
        () => buildValidationFeatures(selectedPlots).length,
        [buildValidationFeatures, selectedPlots],
    );

    const [showAsApplied, setShowAsApplied] = useState(false);

    const hasAsApplied = useMemo(() => {
        return (
            selectedPlots.length > 0 &&
            !!selectedPlots[0]?.properties?.latest_application_id
        );
    }, [selectedPlots]);

    const hasConfirmation = useMemo(() => {
        return (
            selectedPlots.filter((plot) => plot.properties.confirmed).length > 0
        );
    }, [selectedPlots]);

    const imageQuery = useGetRequest({
        url: `application_operations/${[...selectedPlots][0]?.properties?.latest_application_id}/map_display_image/`,
        responseType: "blob",
        enabled: hasAsApplied,
    });

    const boundsQuery = useGetRequest<number[]>({
        url: `application_operations/${[...selectedPlots][0]?.properties?.latest_application_id}/bounds/`,
        enabled: hasAsApplied,
    });

    const asAppliedPlotsQuery = useGetRequest<
        FeatureCollection<MultiPolygon, AsAppliedPlot>
    >({
        url: `application_operations/${[...selectedPlots][0]?.properties?.latest_application_id}/as_applied_plots/`,
        enabled: hasAsApplied,
    });

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox || !hasAsApplied) return;

        const imageUrl = !imageQuery.data
            ? null
            : window.URL.createObjectURL(imageQuery.data as Blob);
        const bbox = boundsQuery.data
            ? (invertedTurfBboxToFourCornerCoordinates(
                boundsQuery.data,
            ) as Coordinates)
            : fieldBbox;

        if (imageUrl && showAsApplied) {
            const imSource = map.getSource(AS_APPLIED_SOURCE) as ImageSource;
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: bbox,
                    });
                }
            } else {
                map.addSource(AS_APPLIED_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox,
                });
                map.addLayer({
                    id: AS_APPLIED_LAYER.id,
                    type: "raster",
                    source: AS_APPLIED_SOURCE,
                });
            }
        } else {
            removeLayersIfExists(map, [AS_APPLIED_LAYER.id]);
            removeSourcesIfExists(map, [AS_APPLIED_SOURCE]);
        }
    }, [
        hasAsApplied,
        imageQuery.data,
        fieldBbox,
        map,
        mapLoaded,
        showAsApplied,
        boundsQuery.data,
    ]);

    const onOpacityChange = useCallback(
        (value: number | number[]) => {
            if (!map) return;
            updateLayerOpacity(
                map,
                [AS_APPLIED_LAYER.id],
                Array.isArray(value) ? value[0] : value,
            );
        },
        [map],
    );

    const bringToFront = useCallback(() => {
        if (!draw.current || !mapLoaded || !map) return;

        const plots = draw.current.getAll();
        if (map.hasControl(draw.current)) {
            map.removeControl(draw.current);
        }
        map.addControl(draw.current);
        draw.current.add(plots);
    }, [map, mapLoaded]);

    const pairs = useMemo(() => {
        if (!selectedPlots.length) return [];

        return sortArrayByKey(
            selectedPlots.reduce<PlotTableRow[]>(
                (acc: PlotTableRow[], feature) => {
                    let existingRow = acc.find(
                        (row) => row.pairId === feature.properties.pair_id,
                    );

                    if (feature.properties.plot_type === PlotType.Canary) {
                        existingRow = {
                            ...(existingRow ?? {
                                pairId: feature.properties.pair_id,
                                referencePlot: undefined,
                            }),
                            canaryPlot: feature,
                        };
                    } else {
                        existingRow = {
                            ...(existingRow ?? {
                                pairId: feature.properties.pair_id,
                                canaryPlot: undefined,
                            }),
                            referencePlot: feature,
                        };
                    }

                    const newList = acc.filter(
                        (row) => row.pairId !== existingRow.pairId,
                    );
                    newList.push(existingRow);
                    return newList;
                },
                [],
            ) ?? [],
            "pairId",
        ).map((pair, index) => {
            return {
                key: index.toString(),
                pairId: pair.canaryPlot?.properties.pair_id,
                canaryRate: pair.canaryPlot?.properties.rate_offset ?? "--",
                referenceRate:
                    pair.referencePlot?.properties.rate_offset ?? "--",
                active:
                    pair.canaryPlot?.properties.is_active ??
                    pair.referencePlot?.properties.is_active,
                canary: pair.canaryPlot,
                reference: pair.referencePlot,
            };
        });
    }, [selectedPlots]);

    const onPlotChecked = useCallback((value: boolean, plotIds: number[]) => {
        if (!draw.current) return;
        const plots = draw.current?.getAll();
        const features = plots?.features.map((feature) => {
            if (feature.properties && plotIds.includes(feature.id as number)) {
                feature.properties.is_active = value;
            }

            return feature;
        });

        draw.current.deleteAll();
        draw.current.add({ type: "FeatureCollection", features });
        setSelectedPlots(
            features.filter((f) => !isValidationFeature(f)) as any,
        );
    }, []);

    const onPlotRateChange = useCallback(
        (plotId: number, plotType: PlotType, value: string | number) => {
            if (!draw.current) return;
            const plots = draw.current.getAll();
            const features = plots.features.map((feature) => {
                if (feature.id === plotId && feature.properties) {
                    let rateValue = Number(value);
                    if (plotType === PlotType.Canary && rateValue > 0) {
                        rateValue = -rateValue;
                    }
                    if (plotType === PlotType.Reference && rateValue < 0) {
                        rateValue = Math.abs(rateValue);
                    }
                    feature.properties.rate_offset = rateValue;
                }
                return feature;
            });

            draw.current.deleteAll();
            draw.current.add({ type: "FeatureCollection", features });
            setSelectedPlots(
                features.filter((f) => !isValidationFeature(f)) as any,
            );
        },
        [],
    );

    const onConfirmationChecked = useCallback((value: boolean) => {
        if (!draw.current) return;
        const plots = draw.current?.getAll();
        const features = plots?.features.map((feature) => {
            if (feature.properties && !isValidationFeature(feature)) {
                feature.properties.confirmed = value;
            }

            return feature;
        });

        draw.current.deleteAll();
        draw.current.add({ type: "FeatureCollection", features });
        setSelectedPlots(
            features.filter((f) => !isValidationFeature(f)) as any,
        );
    }, []);

    const savePlotRequest = useMutateRequest({
        method: "post",
        url: "plots/update/",
        successMessage: t("ariaLabels.plotsSavedSuccessfully"),
        errorMessage: t("ariaLabels.failedToSavePlots"),
    });

    const autoCorrectPlots = useCallback(() => {
        if (!draw.current) return;
        const validationFeatures = draw.current
            .getAll()
            .features.filter(isValidationFeature);
        const correctedPlots = selectedPlots.map((plot) => {
            const asApplied = asAppliedPlotsQuery.data?.features.find(
                (ap) => ap.properties.plot === plot.id,
            );
            return {
                ...plot,
                geometry: asApplied?.geometry || plot.geometry,
                properties: {
                    ...plot.properties,
                    flagged: asApplied?.properties.flagged || false,
                },
            };
        });

        draw.current.deleteAll();
        draw.current.add({
            type: "FeatureCollection",
            features: [...correctedPlots, ...validationFeatures],
        });
        bringToFront();
        checkValidationPlots();
    }, [
        asAppliedPlotsQuery.data,
        bringToFront,
        checkValidationPlots,
        selectedPlots,
    ]);

    const saveValidationPlotsRequest = useMutateRequest({
        method: "post",
        url: "plots/update/",
        successMessage: t("messages.validationPlotsSaved"),
        errorMessage: t("messages.failedToSaveValidationPlots"),
    });

    const savePlots = useCallback(() => {
        if (!draw.current) return;
        const features = draw.current.getAll().features;
        const plotFeatures = features.filter(
            (feature) => !isValidationFeature(feature),
        );
        const validationFeatures = features.filter(isValidationFeature);

        const validationPayload = validationFeatures
            .map((feature) => {
                const id = getValidationCanaryPlotId(feature, plotFeatures);
                const validation_boundary = toMultiPolygon(
                    feature.geometry as MultiPolygon | Polygon,
                );
                if (id == null || !validation_boundary) return null;
                return { id, validation_boundary };
            })
            .filter(
                (
                    item,
                ): item is { id: number; validation_boundary: MultiPolygon } =>
                    item != null,
            );

        const saveValidationBoundaries = () => {
            if (!validationPayload.length) return;

            const intersectsPair = validationFeatures.some((feature) => {
                const pairId = feature.properties?.pair_id;
                const canaryId = getValidationCanaryPlotId(
                    feature,
                    plotFeatures,
                );
                const pairPlots = plotFeatures.filter(
                    (plot) =>
                        Number(plot.id) === Number(canaryId) ||
                        (Number(plot.properties?.pair_id) === Number(pairId) &&
                            Number(plot.properties?.plot_type) ===
                            PlotType.Reference),
                ) as Feature<MultiPolygon | Polygon>[];
                if (!feature.geometry || !pairPlots.length) return false;
                return pairPlots.some((plot) =>
                    polygonsOverlap(
                        feature as Feature<MultiPolygon | Polygon>,
                        plot,
                    ),
                );
            });
            if (intersectsPair) return;

            saveValidationPlotsRequest.mutate(
                {
                    data: validationPayload,
                    params: { boundary: "validation" },
                },
                {
                    onSuccess: () => {
                        validationPlotsQuery.refetch();
                    },
                },
            );
        };

        savePlotRequest.mutate(
            {
                data: plotFeatures.map((feature) => ({
                    ...feature.properties,
                    id: feature.id,
                    analytic_boundary: feature.geometry,
                })),
                params: { boundary: "analytic" },
            },
            {
                onSuccess: () => {
                    uploadedPlots.refetch();
                    appliedPlots.refetch();
                    saveValidationBoundaries();
                },
                onError: () => {
                    // Still attempt validation-only edits if analytic save fails.
                    saveValidationBoundaries();
                },
            },
        );
    }, [
        appliedPlots,
        savePlotRequest,
        saveValidationPlotsRequest,
        uploadedPlots,
        validationPlotsQuery,
    ]);

    return (
        <div className="flex flex-col gap-2">
            <div className="font-semibold">
                {t("activeSentinelPlots", { ns: "insights" })}
            </div>
            <Table
                aria-label={t("ariaLabels.plots")}
                selectionMode="single"
                onSelectionChange={(e) => {
                    const key = [...e][0];
                    const plotSet = plotSets?.find((set) => set.key == key);
                    const plots = plotSet?.plots ?? [];
                    drawPlots(plots);
                    setSelectedPlots(plots);
                }}
            >
                <TableHeader columns={COLUMNS}>
                    {(column) => (
                        <TableColumn key={column.key}>
                            {column.label}
                        </TableColumn>
                    )}
                </TableHeader>
                <TableBody
                    items={plotSets}
                    emptyContent={t("messages.noPlotsFound")}
                >
                    {(item) => (
                        <TableRow key={item.key}>
                            {(columnKey) => (
                                <TableCell>
                                    {getKeyValue(item, columnKey)}
                                </TableCell>
                            )}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {!!pairs?.length && (
                <>
                    <div className="flex flex-row gap-2 justify-between">
                        <div className="flex flex-row gap-2 items-center text-sm">
                            {hasAsApplied && !imageQuery.isFetching && (
                                <>
                                    <Checkbox
                                        isSelected={showAsApplied}
                                        onValueChange={setShowAsApplied}
                                        size="sm"
                                    >
                                        {t("asApplied")}
                                    </Checkbox>
                                    <Slider
                                        defaultValue={0.8}
                                        aria-label={t("ariaLabels.opacity")}
                                        className="w-[8rem]"
                                        onChange={onOpacityChange}
                                        minValue={0}
                                        maxValue={1}
                                        step={0.01}
                                        size="sm"
                                    />
                                </>
                            )}
                            <Checkbox
                                isSelected={showValidationPlots}
                                onValueChange={setShowValidationPlots}
                                size="sm"
                            >
                                {t("validationPlots")}
                            </Checkbox>
                            {showValidationPlots &&
                                validationPlotsQuery.isFetched &&
                                !validationPlotsQuery.isFetching &&
                                !validationFeatureCount && (
                                    <span className="text-warning text-xs">
                                        {t("messages.noValidationPlotsForSet")}
                                    </span>
                                )}
                        </div>

                        <div>
                            <Button
                                color="primary"
                                variant="flat"
                                size="sm"
                                onPress={bringToFront}
                            >
                                {t("bringPlotsToTop")}
                            </Button>
                        </div>

                        {!!asAppliedPlotsQuery.data?.features?.length && (
                            <>
                                <div>
                                    <Button
                                        color="primary"
                                        variant="flat"
                                        size="sm"
                                        onPress={autoCorrectPlots}
                                    >
                                        {t("autoCorrectPlots")}
                                    </Button>
                                </div>
                                <div>
                                    <Button
                                        color="primary"
                                        variant="flat"
                                        size="sm"
                                        onPress={() => drawPlots(selectedPlots)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faRotateLeft}
                                            className="mr-2"
                                        />
                                        {t("resetPlots")}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    <Table aria-label={t("ariaLabels.editPlots")}>
                        <TableHeader columns={PLOT_COLUMNS}>
                            {(column) => (
                                <TableColumn key={column.key}>
                                    {column.label}
                                </TableColumn>
                            )}
                        </TableHeader>
                        <TableBody items={pairs}>
                            {(item) => (
                                <TableRow key={item.key}>
                                    {(columnKey) => {
                                        if (columnKey === "canaryRate") {
                                            return (
                                                <TableCell>
                                                    {item.canary &&
                                                        item.canaryRate !== "--" ? (
                                                        <Input
                                                            type="number"
                                                            value={String(
                                                                item.canaryRate,
                                                            )}
                                                            onValueChange={(
                                                                value,
                                                            ) => {
                                                                onPlotRateChange(
                                                                    item.canary
                                                                        ?.id as number,
                                                                    PlotType.Canary,
                                                                    value,
                                                                );
                                                            }}
                                                            min={-90}
                                                            max={
                                                                userIsSentinel ||
                                                                    fullPlotOffsetEnabled
                                                                    ? 0
                                                                    : -30
                                                            }
                                                            color="danger"
                                                            size="sm"
                                                        />
                                                    ) : (
                                                        "--"
                                                    )}
                                                </TableCell>
                                            );
                                        }

                                        if (columnKey === "referenceRate") {
                                            return (
                                                <TableCell>
                                                    {item.reference &&
                                                        item.referenceRate !==
                                                        "--" ? (
                                                        <Input
                                                            type="number"
                                                            value={String(
                                                                item.referenceRate,
                                                            )}
                                                            onValueChange={(
                                                                value,
                                                            ) => {
                                                                onPlotRateChange(
                                                                    item
                                                                        .reference
                                                                        ?.id as number,
                                                                    PlotType.Reference,
                                                                    value,
                                                                );
                                                            }}
                                                            min={
                                                                userIsSentinel ||
                                                                    fullPlotOffsetEnabled
                                                                    ? 0
                                                                    : 30
                                                            }
                                                            max={90}
                                                            color="success"
                                                            size="sm"
                                                        />
                                                    ) : (
                                                        "--"
                                                    )}
                                                </TableCell>
                                            );
                                        }

                                        if (columnKey === "active") {
                                            const isActive = getKeyValue(
                                                item,
                                                columnKey,
                                            );
                                            return (
                                                <TableCell>
                                                    <Checkbox
                                                        isSelected={isActive}
                                                        onValueChange={(
                                                            selected,
                                                        ) => {
                                                            onPlotChecked(
                                                                selected,
                                                                [
                                                                    item.canary
                                                                        ?.id as number,
                                                                    item
                                                                        .reference
                                                                        ?.id as number,
                                                                ],
                                                            );
                                                        }}
                                                    />
                                                </TableCell>
                                            );
                                        }

                                        return (
                                            <TableCell>
                                                {getKeyValue(item, columnKey)}
                                            </TableCell>
                                        );
                                    }}
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <Checkbox
                        isSelected={hasConfirmation}
                        onValueChange={onConfirmationChecked}
                    >
                        {t("confirmPlotsAsAccurate", {
                            ns: "insights",
                        })}
                    </Checkbox>
                    {!!validationIssues.length && (
                        <div className="flex flex-col gap-1 text-sm">
                            {validationIssues.map((issue) => (
                                <div
                                    key={`${issue.featureId}-${issue.reason}-${issue.message}`}
                                    className={
                                        issue.reason === "intersection"
                                            ? "text-danger"
                                            : "text-warning"
                                    }
                                >
                                    {issue.message}
                                </div>
                            ))}
                            {hasValidationIntersection && (
                                <div className="text-danger font-medium">
                                    {t(
                                        "messages.validationPlotIntersectionBlocksSave",
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <Button
                        className="self-end"
                        color="secondary"
                        variant="solid"
                        size="sm"
                        onPress={savePlots}
                        startContent={<FontAwesomeIcon icon={faSave} />}
                        isLoading={
                            savePlotRequest.isPending ||
                            saveValidationPlotsRequest.isPending
                        }
                        isDisabled={hasValidationIntersection}
                        fullWidth={true}
                    >
                        {t("save")}
                    </Button>
                </>
            )}
        </div>
    );
}

export default ActivePlots;
